import { UploadStatuses } from '@trackman/baseball-shared-components';
import { type IUploaderEstimatesExtendedModel } from '@trackman/baseball-shared-components/build/components/DisplayUploadStatus/interfaces/IUploaderEstimatesExtendedModel';
import { IUploaderEstimatesModel } from '@trackman/baseball-shared-components/build/components/DisplayUploadStatus/interfaces/IUploaderEstimatesModel';
import { action, makeAutoObservable, observable } from 'mobx';

export default class VideoUploaderStateStore {
  @observable measurementsAndMetadata: IUploaderEstimatesExtendedModel;

  @observable compositeImages: IUploaderEstimatesExtendedModel;

  @observable playerPositioningData: IUploaderEstimatesExtendedModel;

  @observable playVideo: IUploaderEstimatesExtendedModel;

  @observable batTrackingVideo: IUploaderEstimatesExtendedModel;

  @observable mainComponentUploadStatus: UploadStatuses | string;

  @observable totalTimeRemaining: string;

  @observable isVisible: boolean;

  @observable time: Date | string;

  @observable isInternetConnectionAvailable: boolean = false;

  constructor() {
    const initializer = {
      uploaderEstimatesType: '',
      awaitingUploadCount: 0,
      estimatedSizeMBytes: 0,
      estimatedTimeMiliseconds: 0,
      isEnabled: false,
      estimatedSizeFormatted: '',
      estimatedTimeFormatted: '',
      uploadStatus: UploadStatuses.AwaitingData,
      isVisible: false,
      totalCount: 0,
    } as IUploaderEstimatesExtendedModel;

    this.measurementsAndMetadata = {
      ...initializer,
    };

    this.compositeImages = {
      ...initializer,
    };

    this.playerPositioningData = {
      ...initializer,
    };

    this.playVideo = {
      ...initializer,
    };

    this.batTrackingVideo = {
      ...initializer,
    };

    this.isVisible = false;
    this.mainComponentUploadStatus = UploadStatuses.AwaitingData;
    this.totalTimeRemaining = '';
    this.time = '';

    makeAutoObservable(this);
  }

  @action setMeasurementsAndMetadata = (data: IUploaderEstimatesModel): void => {
    this.measurementsAndMetadata = this.createEstimates(data);
  };

  @action setCompositeImages = (data: IUploaderEstimatesModel): void => {
    this.compositeImages = this.createEstimates(data);
  };

  @action setPlayerPositioningData = (data: IUploaderEstimatesModel): void => {
    this.playerPositioningData = this.createEstimates(data);
  };

  @action setPlayVideo = (data: IUploaderEstimatesModel): void => {
    this.playVideo = this.createEstimates(data);
  };

  @action setBatTrackingVideo = (data: IUploaderEstimatesModel): void => {
    this.batTrackingVideo = this.createEstimates(data);
  };

  @action setMainComponentUploadStatus = (uploadStatus: UploadStatuses | string) => {
    this.mainComponentUploadStatus = uploadStatus;
  };

  @action setTime = (time: string | Date) => {
    this.time = time;
  };

  @action setTotalTimeRemaining = (time: number[]) => {
    this.totalTimeRemaining = this.secondsToHms(time.reduce((a, b) => a + b, 0) / 1000);
  };

  @action setIsVisible = (isVisible: boolean): void => {
    this.isVisible = isVisible;
  };

  @action setIsInternetConnectionAvailable = (isAvailable: boolean): void => {
    this.isInternetConnectionAvailable = isAvailable;
  };

  private createEstimates = (data: IUploaderEstimatesModel): IUploaderEstimatesExtendedModel => {
    return {
      ...data,
      estimatedTimeFormatted: this.estimatedTimeFormatted(data.estimatedTimeMiliseconds, data.awaitingUploadCount),
      estimatedSizeFormatted: this.estimatedSizeFormatted(data.estimatedSizeMBytes),
      uploadStatus: data.uploadStatus,
      isVisible: data.uploadStatus !== UploadStatuses.Disabled,
    };
  };

  private estimatedTimeFormatted = (estimatedTimeSec: number, awaitingUploadCount: number): string => {
    if (!estimatedTimeSec || estimatedTimeSec === 0) {
      if (awaitingUploadCount > 0) {
        return 'unknown';
      }
      return '0 s';
    }
    return this.secondsToHms(estimatedTimeSec / 1000);
  };

  private estimatedSizeFormatted = (estimatedSizeMBytes: number): string => {
    if (!estimatedSizeMBytes || estimatedSizeMBytes === 0) {
      return '0 Bytes';
    }

    const sizeInBytes = estimatedSizeMBytes * 1024 * 1024;
    const e = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return `${(sizeInBytes / Math.pow(1024, e)).toFixed(2)} ${' KMGTP'.charAt(e)}B`;
  };

  private secondsToHms = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor((seconds % 3600) % 60);

    const hDisplay = h > 0 ? h + (h === 1 ? ' h, ' : ' h, ') : '';
    const mDisplay = m > 0 ? m + (m === 1 ? ' m, ' : ' m, ') : '';
    const sDisplay = s > 0 ? s + (s === 1 ? ' s' : ' s') : '';
    return hDisplay + mDisplay + sDisplay;
  };
}
